/**
 * Email Activation Service
 * Delegates all email sending to the backend. The frontend never constructs
 * email HTML or sends SMTP traffic directly.
 *
 * ─── Backend API Contract ────────────────────────────────────────────────────
 *
 * POST /api/v1/email-activation/send
 *   Triggered once, immediately after a successful user registration.
 *   Request body:
 *     {
 *       "email":        string,   // recipient address (required)
 *       "user_id":      string,   // ID from registration response (optional — when
 *                                 // omitted the backend must look up the user by email)
 *       "template":     "welcome",
 *       "frontend_url": string    // app origin, e.g. https://app.optibuild.pro
 *                                 // backend must embed the activation link as:
 *                                 //   {frontend_url}/verify-email?code={CODE}
 *     }
 *   Success response (200):
 *     {
 *       "message":               string,
 *       "activation_code_sent":  true,
 *       "expires_at":            ISO-8601 datetime
 *     }
 *
 * POST /api/v1/email-activation/resend
 *   Triggered by the user clicking "Resend activation email" on the verify page.
 *   Request body:
 *     {
 *       "email":        string,   // recipient address (required)
 *       "template":     "resend",
 *       "frontend_url": string    // same app origin as above; link format same as /send
 *     }
 *   Success response (200): same shape as /send
 *
 * POST /api/v1/email-activation/validate
 *   Validates the activation code the user types in.
 *   Request body:
 *     {
 *       "email":           string,  // must be lower-cased & trimmed
 *       "activation_code": string   // 6-8 uppercase alphanumeric characters
 *     }
 *   Success response (200):
 *     {
 *       "message":        string,
 *       "email_verified": true,
 *       "user_id":        string,
 *       "access_token":   string | null  — null until Keycloak realm is configured
 *       "refresh_token":  string | null    for token-exchange; frontend redirects to
 *                                          login when null
 *     }
 *
 * DELETE /api/v1/email-activation/cleanup
 *   Admin/internal endpoint to remove expired activation codes.
 *   No request body required.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { httpClient, ApiResponse } from './http.client';
import { getModuleEndpoint } from '../config/config.loader';

// ── Request / Response shapes ─────────────────────────────────────────────────

export interface SendActivationRequest {
  email: string;
  /** Provided when called immediately after registration; omit to let the backend look up the user by email. */
  user_id?: string;
  template?: 'welcome';
}

export interface ResendActivationRequest {
  email: string;
  template?: 'resend';
}

export interface SendActivationResponse {
  message: string;
  activation_code_sent: boolean;
  expires_at: string;
}

export interface ValidateCodeRequest {
  email: string;
  activation_code: string;
}

export interface ValidateCodeResponse {
  message: string;
  email_verified: boolean;
  user_id: string;
  access_token?: string | null;
  refresh_token?: string | null;
  /** Short-lived token (TTL ~10 min) the frontend exchanges for a password set. */
  set_password_token?: string;
}

export interface EmailActivationError {
  error: string;
  error_description?: string;
  message: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

class EmailActivationService {
  /**
   * Send the initial welcome / activation email right after registration.
   * Calls POST /api/v1/email-activation/send — the backend is responsible for
   * generating the activation code, persisting it, and delivering the email.
   */
  async sendActivationEmail(
    request: SendActivationRequest
  ): Promise<ApiResponse<SendActivationResponse>> {
    try {
      const endpoint = getModuleEndpoint('authentication', 'emailActivationSend');

      return await httpClient.post<SendActivationResponse>(
        endpoint,
        {
          email: request.email,
          user_id: request.user_id,
          template: request.template ?? 'welcome',
          frontend_url: window.location.origin,
        },
        { requireAuth: false, timeout: 60000, retries: 0 }
      );
    } catch (error) {
      console.error('Send activation email failed:', error);
      throw this.normaliseError(error);
    }
  }

  /**
   * Resend the activation email when the user requests it on the verify page.
   * Calls POST /api/v1/email-activation/resend.
   */
  async resendActivationEmail(
    email: string
  ): Promise<ApiResponse<SendActivationResponse>> {
    try {
      const endpoint = getModuleEndpoint('authentication', 'emailActivationResend');

      return await httpClient.post<SendActivationResponse>(
        endpoint,
        {
          email: email.toLowerCase().trim(),
          template: 'resend',
          frontend_url: window.location.origin,
        },
        { requireAuth: false, timeout: 60000, retries: 0 }
      );
    } catch (error) {
      console.error('Resend activation email failed:', error);
      throw this.normaliseError(error);
    }
  }

  /**
   * Validate the activation code the user entered.
   * Calls POST /api/v1/email-activation/validate.
   */
  async validateActivationCode(
    request: ValidateCodeRequest
  ): Promise<ApiResponse<ValidateCodeResponse>> {
    try {
      const endpoint = getModuleEndpoint('authentication', 'emailActivationValidate');

      return await httpClient.post<ValidateCodeResponse>(
        endpoint,
        {
          email: request.email.toLowerCase().trim(),
          activation_code: request.activation_code.trim(),
        },
        { requireAuth: false }
      );
    } catch (error) {
      console.error('Validate activation code failed:', error);
      throw this.normaliseError(error);
    }
  }

  // ── URL helpers used by the UI ──────────────────────────────────────────────

  /**
   * Extract activation params from the current URL.
   * Backend format: /verify-email?code=CODE&email=EMAIL%40domain.com
   * Legacy format:  /activate?email=EMAIL&code=CODE
   * When both code and email are present the page auto-validates without user input.
   */
  extractActivationParams(): { email?: string; code: string } | null {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      if (!code) return null;
      const email = params.get('email') ?? undefined;
      return { code: decodeURIComponent(code), email: email ? decodeURIComponent(email) : undefined };
    } catch {
      return null;
    }
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  isValidActivationCode(code: string): boolean {
    return /^[A-Z0-9]{6,8}$/.test(code.toUpperCase());
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private normaliseError(error: any): EmailActivationError {
    if (error?.response?.data) {
      return {
        error: error.response.data.error ?? 'activation_error',
        error_description: error.response.data.error_description,
        message: error.response.data.message ?? 'Email activation failed',
      };
    }
    return {
      error: error?.message ? 'network_error' : 'unknown_error',
      message: error?.message ?? 'An unexpected error occurred during email activation',
    };
  }
}

export const emailActivationService = new EmailActivationService();
